/**
 * Transfer Hook
 * 송금 페이지 데이터 관리 및 API 호출 로직
 */

import { useCallback, useEffect, useState } from 'react';
import { getWithdrawalAccount } from '../../withdrawal/api/withdrawalApi';
import { requestTransfer, getTransferTargets } from '../api/transferApi';
import { WithdrawalAccountResponse } from '../../withdrawal/types/withdrawal';
import {
    TransferTarget,
    TransferRequest,
    TransferResponse,
} from '../types/transfer';

/**
 * 송금 페이지 Hook
 */
export const useTransfer = () => {
    // 상태 관리
    const [accountInfo, setAccountInfo] = useState<WithdrawalAccountResponse | null>(null);
    const [targets, setTargets] = useState<TransferTarget[]>([]);
    const [selectedTarget, setSelectedTarget] = useState<TransferTarget | null>(null);

    // API 호출 상태 관리
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isTransferring, setIsTransferring] = useState<boolean>(false);
    const [transferError, setTransferError] = useState<Error | null>(null);

    /**
     * 계좌 정보 조회 (잔액 조회용)
     */
    const fetchAccountInfo = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const responseData = await getWithdrawalAccount();

            if (responseData.success) {
                setAccountInfo(responseData);
            } else {
                setError(new Error(responseData.message || '계좌 정보 조회에 실패했습니다.'));
            }
        } catch (error) {
            setError(error as Error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * 송금 대상 목록 조회
     */
    const searchTargets = useCallback(async (keyword?: string) => {
        setIsSearching(true);

        try {
            const responseData = await getTransferTargets({ keyword });
            setTargets(responseData || []);
        } catch (error) {
            setTargets([]);
        } finally {
            setIsSearching(false);
        }
    }, []);

    /**
     * 송금 요청
     */
    const handleTransfer = useCallback(async (
        reqVal: number,
        targetId: string,
        targetType: 'R' | 'S',
        briefs = ''
    ): Promise<TransferResponse | null> => {
        setIsTransferring(true);
        setTransferError(null);

        try {
            const requestData: TransferRequest = {
                reqVal,
                targetId,
                targetType,
                briefs,
            };

            const responseData = await requestTransfer(requestData);

            if (responseData.success) {
                return responseData;
            } else {
                const errorMessage = responseData.message || '송금에 실패했습니다.';
                setTransferError(new Error(errorMessage));
                return responseData;
            }
        } catch (error) {
            console.error('송금 실패:', error);
            setTransferError(error as Error);
            return null;
        } finally {
            setIsTransferring(false);
        }
    }, []);

    /**
     * 선택된 대상 초기화
     */
    const clearSelectedTarget = useCallback(() => {
        setSelectedTarget(null);
    }, []);

    // 컴포넌트 마운트 시 계좌 정보 및 송금 대상 목록 조회
    useEffect(() => {
        fetchAccountInfo();
        searchTargets();
    }, [fetchAccountInfo, searchTargets]);

    return {
        // 상태
        accountInfo,
        targets,
        selectedTarget,
        setSelectedTarget,

        // 로딩 상태
        isLoading,
        error,
        isSearching,
        isTransferring,
        transferError,

        // 핸들러
        fetchAccountInfo,
        searchTargets,
        handleTransfer,
        clearSelectedTarget,
    };
};
